"use strict";
var _a, _b, _c;
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var Icons_1 = require("../icons/Icons");
var FocusIndicators_1 = require("../styles/FocusIndicators");
var PreventUserSelect_1 = require("../styles/PreventUserSelect");
var TabStyles_1 = require("../styles/TabStyles");
var Animation_1 = require("../utils/Animation");
var Declarative_1 = require("../utils/Declarative");
var DomUtil_1 = require("../utils/DomUtil");
var FocusIndicator_1 = require("../utils/FocusIndicator");
var InteractiveDom_1 = require("../utils/InteractiveDom");
var ReactHelpers_1 = require("../utils/ReactHelpers");
var DomWidgets_1 = require("./DomWidgets");
var Spacing = TabStyles_1.TabStyles.Spacing;
var ExpandoDirection;
(function (ExpandoDirection) {
    ExpandoDirection[ExpandoDirection["Up"] = 0] = "Up";
    ExpandoDirection[ExpandoDirection["Down"] = 1] = "Down";
    ExpandoDirection[ExpandoDirection["Left"] = 2] = "Left";
    ExpandoDirection[ExpandoDirection["Right"] = 3] = "Right";
})(ExpandoDirection = exports.ExpandoDirection || (exports.ExpandoDirection = {}));
var CollapsedIconTransform = Object.freeze((_a = {},
    _a[ExpandoDirection.Up] = 'rotate(180deg)',
    _a[ExpandoDirection.Right] = 'rotate(90deg)',
    _a[ExpandoDirection.Down] = 'rotate(180deg)',
    _a[ExpandoDirection.Left] = 'rotate(270deg)',
    _a));
var ExpandedIconTransform = Object.freeze((_b = {},
    _b[ExpandoDirection.Up] = 'rotate(0deg)',
    _b[ExpandoDirection.Right] = CollapsedIconTransform[ExpandoDirection.Left],
    _b[ExpandoDirection.Down] = 'rotate(0deg)',
    _b[ExpandoDirection.Left] = CollapsedIconTransform[ExpandoDirection.Right],
    _b));
var ExpandoFlexDirection = (_c = {},
    _c[ExpandoDirection.Up] = 'column-reverse',
    _c[ExpandoDirection.Down] = 'column',
    _c[ExpandoDirection.Left] = 'row-reverse',
    _c[ExpandoDirection.Right] = 'row',
    _c);
var ExpandoButtonStyleName = TypeStyle.style(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, PreventUserSelect_1.PreventUserSelect), FocusIndicators_1.PreFocusIndicator), { alignItems: 'center', cursor: 'pointer', display: 'flex' }));
/**
 * **NOTE**: all HTML props are applied to the focusable button element, with the exception of `style` and `className`, which are applied
 * to the root wrapper element.
 */
var ExpandoWidget = /** @class */ (function (_super) {
    tslib_1.__extends(ExpandoWidget, _super);
    function ExpandoWidget() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.expandoBaseTestId = 'Expando';
        _this.contentRef = React.createRef();
        _this.contentId = DomUtil_1.generateId();
        _this.state = {
            hoveringOnExpandoButton: false,
            shouldShowContent: _this.props.isExpanded,
            animationTimeoutId: undefined,
        };
        _this.handleClick = function (e) {
            _this.handleExpandOrCollapse();
        };
        _this.handleKey = function (e) {
            if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                if (e.type === 'keyup') {
                    _this.handleExpandOrCollapse();
                }
            }
        };
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(_this.handleKey, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleKeyUp = ReactHelpers_1.mergeEventHandlers(_this.handleKey, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyUp) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleHover = function (hover) {
            _this.setState({ hoveringOnExpandoButton: hover });
        };
        _this.handleAnimationComplete = function () {
            _this.setState({ animationTimeoutId: undefined });
            if (_this.state.shouldShowContent !== _this.props.isExpanded) {
                _this.setState({ shouldShowContent: _this.props.isExpanded });
            }
        };
        return _this;
    }
    ExpandoWidget.getDerivedStateFromProps = function (props) {
        // We can't always set shouldShowContent: props.isExpanded, because otherwise
        // the content would hide as the collapse animation starts.
        // We instead set shouldShowContent:false only after the collapse animation completes in handleAnimationComplete()
        if (props.isExpanded) {
            return { shouldShowContent: true };
        }
        else {
            return {};
        }
    };
    ExpandoWidget.prototype.componentDidUpdate = function () {
        this.updateHeightOrWidth();
    };
    ExpandoWidget.prototype.componentDidMount = function () {
        this.updateHeightOrWidth();
    };
    ExpandoWidget.prototype.componentWillUnmount = function () {
        window.clearTimeout(this.state.animationTimeoutId);
        this.setState({ animationTimeoutId: undefined });
    };
    ExpandoWidget.prototype.handleExpandOrCollapse = function () {
        if (this.state.animationTimeoutId) {
            return;
        }
        if (this.props.isExpanded) {
            this.props.onCollapse();
        }
        else {
            this.props.onExpand();
        }
    };
    ExpandoWidget.prototype.updateHeightOrWidth = function () {
        var contentDiv = this.contentRef.current;
        if (!contentDiv || this.state.animationTimeoutId) {
            return;
        }
        var animateTo = {
            onComplete: this.handleAnimationComplete.bind(this),
            opacity: this.props.isExpanded ? 1 : 0,
        };
        var willAnimate = false;
        function checkWillAnimate(currentProp, newProp) {
            return !!currentProp && currentProp.replace('px', '') !== newProp;
        }
        if (this.isVertical()) {
            animateTo.height = this.props.isExpanded ? contentDiv.scrollHeight : 0;
            if (checkWillAnimate(contentDiv.style.height, animateTo.height.toString())) {
                willAnimate = true;
            }
            contentDiv.style.height = animateTo.height + 'px';
        }
        else {
            animateTo.width = this.props.isExpanded ? contentDiv.scrollWidth : 0;
            if (checkWillAnimate(contentDiv.style.width, animateTo.width.toString())) {
                willAnimate = true;
            }
            contentDiv.style.width = animateTo.width + 'px';
        }
        contentDiv.style.opacity = animateTo.opacity.toString();
        if (willAnimate) {
            this.setState({
                animationTimeoutId: window.setTimeout(this.handleAnimationComplete, Animation_1.AnimationDuration)
            });
        }
    };
    ExpandoWidget.prototype.isVertical = function () {
        return this.props.direction === ExpandoDirection.Up || this.props.direction === ExpandoDirection.Down;
    };
    ExpandoWidget.prototype.getExpandoStyle = function () {
        return {
            display: 'flex',
            flexDirection: ExpandoFlexDirection[this.props.direction],
            alignItems: 'flex-start',
        };
    };
    ExpandoWidget.prototype.getExpandoIconStyle = function () {
        return {
            height: Icons_1.ExpandoIcon.height,
            margin: 'auto',
            marginLeft: 0,
            marginRight: this.props.label ? Spacing.DefaultSpace : 0,
            transform: this.props.isExpanded ? ExpandedIconTransform[this.props.direction] : CollapsedIconTransform[this.props.direction],
            transition: "transform, " + Animation_1.AnimationDuration + "ms",
            width: Icons_1.ExpandoIcon.width,
        };
    };
    ExpandoWidget.prototype.getExpandoContentStyle = function () {
        return {
            overflow: this.props.isExpanded && !this.state.animationTimeoutId ? undefined : 'hidden',
            position: 'relative',
            transition: (this.isVertical() ? 'height' : 'width') + " " + Animation_1.AnimationDuration + "ms ease-out,\n                   opacity " + Animation_1.AnimationDuration + "ms ease-out",
        };
    };
    ExpandoWidget.prototype.render = function () {
        var _a = this.props, children = _a.children, className = _a.className, direction = _a.direction, isExpanded = _a.isExpanded, label = _a.label, onCollapse = _a.onCollapse, onExpand = _a.onExpand, style = _a.style, testId = _a.testId, htmlProps = tslib_1.__rest(_a, ["children", "className", "direction", "isExpanded", "label", "onCollapse", "onExpand", "style", "testId"]);
        return (React.createElement("div", { className: className, "data-tb-test-id": Declarative_1.makeTestId(testId, this.expandoBaseTestId), style: tslib_1.__assign(tslib_1.__assign({}, this.getExpandoStyle()), style) },
            React.createElement(InteractiveDom_1.InteractiveDomWrapper, { key: 'expandoInteractiveDom', handleHover: this.handleHover, pointerClick: this.handleClick },
                React.createElement(FocusIndicator_1.__UNSTABLE__INTERNAL__FocusIndicator, null,
                    React.createElement("div", tslib_1.__assign({}, htmlProps, { "aria-controls": this.contentId, "aria-expanded": isExpanded, className: ExpandoButtonStyleName, "data-tb-test-id": Declarative_1.makeTestId(testId, this.expandoBaseTestId, 'Button'), onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, role: 'button', tabIndex: 0 }),
                        React.createElement(DomWidgets_1.Img, { key: 'expandoIcon', "data-tb-test-id": Declarative_1.makeTestId(testId, this.expandoBaseTestId, 'Icon'), src: Icons_1.ExpandoIcon.icon({ active: this.state.hoveringOnExpandoButton }), style: this.getExpandoIconStyle() }),
                        label &&
                            React.createElement("div", { key: 'expandoLabel', "data-tb-test-id": Declarative_1.makeTestId(testId, this.expandoBaseTestId, 'Label') }, label)))),
            React.createElement("div", { key: 'expandoContent', "data-tb-test-id": Declarative_1.makeTestId(testId, this.expandoBaseTestId, 'Content'), 
                // We hide content if collapsed to prevent children from getting focus when they shouldn't
                hidden: !this.state.shouldShowContent, id: this.contentId, ref: this.contentRef, style: this.getExpandoContentStyle() }, children)));
    };
    return ExpandoWidget;
}(React.Component));
exports.ExpandoWidget = ExpandoWidget;
//# sourceMappingURL=ExpandoWidget.js.map